/* Piece geometry for the stage. Outlines are the motion study's (design/motion/vectress-motion-study.html); which pole
   each side face carries is read from the engine's tables, never typed in here, so the picture cannot disagree with the
   rules. packages/engine/test/artwork.test.ts runs validateArtwork() to prove it. */
import { FRAMES, FRAME_VEC, SHAPES } from '../../../packages/engine/src/index.ts';
import type { ShapeId } from '../../../packages/engine/src/index.ts';

export interface EdgeFace {
  /** outline vertex indices */
  a: number;
  b: number;
  /** frame direction this face looks in */
  frame: number;
  pole: 1 | -1;
}
export interface PieceGeometry {
  /** outline in the piece's own frame: x right, y down the screen, so -y is toward the opponent; one cell = 1 unit */
  poly: Array<[number, number]>;
  edges: EdgeFace[];
}

/** The study drew pieces at 27 px = 0.40 of a cell. */
const K = 0.40 / 27;
const scaled = (pts: number[][]): Array<[number, number]> => pts.map(([x, y]) => [x * K, y * K]);
const octagon = (): number[][] => {
  const o: number[][] = [];
  for (let i = 0; i < 8; i++) { const a = (-112.5 + 45 * i) * Math.PI / 180; o.push([27 * Math.cos(a), 27 * Math.sin(a)]); }
  return o;
};

const OUTLINE: Record<ShapeId, Array<[number, number]>> = {
  0: scaled([[0, -24], [24, 17], [-24, 17]]),
  1: scaled([[-22, -22], [22, -22], [22, 22], [-22, 22]]),
  2: scaled([[0, -27], [27, 0], [0, 27], [-27, 0]]),
  3: scaled(octagon()),
};

/** The frame direction closest to a local outward direction. */
function frameForDirection(x: number, y: number): number {
  const len = Math.hypot(x, y) || 1;
  let best = 0, bestDot = -Infinity;
  FRAME_VEC.forEach(([dr, dc], i) => {
    const fl = Math.hypot(dr, dc);             // diagonals are (1,1), so normalise before comparing
    const d = (dc * x + -dr * y) / (len * fl);  // frame vector in screen orientation: x = dc, y = -dr
    if (d > bestDot) { bestDot = d; best = i; }
  });
  return best;
}

function build(shape: ShapeId): PieceGeometry {
  const poly = OUTLINE[shape];
  const cx = poly.reduce((s, p) => s + p[0], 0) / poly.length;
  const cy = poly.reduce((s, p) => s + p[1], 0) / poly.length;
  const edges: EdgeFace[] = poly.map((p, a) => {
    const b = (a + 1) % poly.length;
    const frame = frameForDirection((p[0] + poly[b][0]) / 2 - cx, (p[1] + poly[b][1]) / 2 - cy);
    return { a, b, frame, pole: SHAPES[shape].pole[frame] === -1 ? -1 : 1 };
  });
  return { poly, edges };
}

export const GEOMETRY: Record<ShapeId, PieceGeometry> = { 0: build(0), 1: build(1), 2: build(2), 3: build(3) };

/** Every side face must be a true edge of the right pole in the tables, and every table edge must have a face. */
export function validateArtwork(): string[] {
  const problems: string[] = [];
  for (const s of SHAPES) {
    const g = GEOMETRY[s.id];
    const seen = new Set<number>();
    for (const e of g.edges) {
      const where = `${s.name} face toward ${FRAMES[e.frame]}`;
      if (!s.edge[e.frame]) problems.push(`${where}: the table has a corner there, not an edge`);
      if (seen.has(e.frame)) problems.push(`${where}: two faces look the same way`);
      seen.add(e.frame);
      if (s.pole[e.frame] === 0) problems.push(`${where}: neutral in the table, which a coloured face cannot show`);
      else if (s.pole[e.frame] !== e.pole) problems.push(`${where}: face is ${e.pole > 0 ? '+' : '-'} but the table says ${s.pole[e.frame] > 0 ? '+' : '-'}`);
    }
    s.edge.forEach((isEdge, f) => { if (isEdge && !seen.has(f)) problems.push(`${s.name}: the table has an edge toward ${FRAMES[f]} but the outline has no face there`); });
  }
  return problems;
}
